import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { FaStar, FaRegStar, FaQuoteLeft } from 'react-icons/fa';

const ReviewSection = ({ showForm = true }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        rating: 0,
        comment: ''
    });
    const [hoverRating, setHoverRating] = useState(0);
    const [scrollPosition, setScrollPosition] = useState(0);

    // Fetch reviews on component mount
    useEffect(() => {
        fetchReviews();
    }, []);

    // Auto-scroll effect when showForm is false (read-only mode)
    useEffect(() => {
        if (!showForm && reviews.length > 0) {
            const interval = setInterval(() => {
                setScrollPosition((prev) => (prev + 1) % reviews.length);
            }, 5000); // Slower scroll for professional feel (5s)

            return () => clearInterval(interval);
        }
    }, [showForm, reviews.length]);

    const fetchReviews = async () => {
        const baseUrl = process.env.REACT_APP_API_URL || 'https://auth.kgvl.co.in';
        try {
            const response = await axios.get(`${baseUrl}/api/reviews`);
            if (response.data.success) {
                setReviews(response.data.reviews);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const averageRating = useMemo(() => {
        if (reviews.length === 0) return 0;
        const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
        return (sum / reviews.length).toFixed(1);
    }, [reviews]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleRatingClick = (rating) => {
        setFormData({ ...formData, rating });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Please enter your name');
            return;
        }
        if (formData.rating === 0) {
            toast.error('Please select a rating');
            return;
        }
        if (!formData.comment.trim()) {
            toast.error('Please write a comment');
            return;
        }

        setSubmitting(true);

        const baseUrl = process.env.REACT_APP_API_URL || 'https://auth.kgvl.co.in';
        try {
            const response = await axios.post(`${baseUrl}/api/reviews`, {
                name: formData.name,
                rating: formData.rating,
                comment: formData.comment
            });

            if (response.data.success) {
                toast.success('Thank you for your review!', {
                    position: 'bottom-right',
                    autoClose: 3000
                });

                setHasSubmittedReview(true);
                setFormData({ name: '', rating: 0, comment: '' });
                fetchReviews();
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error(error.response?.data?.message || 'Failed to submit review. Please try again.', {
                position: 'bottom-right',
                autoClose: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    const StarRating = ({ rating, interactive = false, onRatingChange, size = "text-xl" }) => {
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => interactive && onRatingChange(star)}
                        onMouseEnter={() => interactive && setHoverRating(star)}
                        onMouseLeave={() => interactive && setHoverRating(0)}
                        className={`${interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}`}
                        disabled={!interactive}
                    >
                        {star <= (interactive ? (hoverRating || rating) : rating) ? (
                            <FaStar className={`text-yellow-400 ${size}`} />
                        ) : (
                            <FaRegStar className={`text-gray-300 ${size}`} />
                        )}
                    </button>
                ))}
            </div>
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div className="bg-white py-20 px-4 font-sans border-t border-gray-100">
            <ToastContainer />

            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-16 space-y-4">
                    <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-green-600">Customer Voices</h2>
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                        Real Experiences, <span className="text-green-600 italic">Real Feedback</span>
                    </h1>
                    <div className="flex flex-col items-center gap-4 pt-4">
                        <div className="flex items-center gap-3">
                            <span className="text-3xl font-bold text-gray-900">{averageRating || "0.0"}</span>
                            <StarRating rating={Math.round(Number(averageRating))} size="text-2xl" />
                        </div>
                        <div className="flex items-center gap-2 px-4 py-1.5 bg-green-50 border border-green-100 rounded-full">
                            <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-widest text-green-700">Verified Customer Feedback</span>
                        </div>
                    </div>
                </div>

                {/* Add Review Form */}
                {showForm && !hasSubmittedReview && (
                    <div className="max-w-2xl mx-auto bg-gray-50 rounded-3xl p-8 md:p-12 mb-20 border border-gray-100 transition-all hover:border-green-100 hover:shadow-xl">
                        <h2 className="text-2xl font-bold text-gray-800 mb-8 border-b border-gray-200 pb-4">Write a Review</h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Your Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Rahul Sharma"
                                        className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent outline-none transition-all placeholder:text-gray-300"
                                        disabled={submitting}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Your Rating</label>
                                    <div className="h-[58px] flex items-center bg-white border border-gray-200 rounded-xl px-4">
                                        <StarRating
                                            rating={formData.rating}
                                            interactive={true}
                                            onRatingChange={handleRatingClick}
                                            size="text-2xl"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2 text-left">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Your Product Review</label>
                                <textarea
                                    name="comment"
                                    value={formData.comment}
                                    onChange={handleInputChange}
                                    placeholder="Share your experience with our product..."
                                    rows="5"
                                    className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-400 focus:border-transparent outline-none transition-all resize-none placeholder:text-gray-300"
                                    disabled={submitting}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-gray-900 text-white font-bold py-5 rounded-xl hover:bg-green-600 transition-all duration-300 transform hover:translate-y-[-2px] hover:shadow-2xl disabled:opacity-50 tracking-widest uppercase text-sm"
                            >
                                {submitting ? 'Processing...' : 'Publish My Review'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Reviews Carousel */}
                <div className="relative group">
                    {loading ? (
                        <div className="flex flex-col items-center py-20 animate-pulse">
                            <div className="w-12 h-12 rounded-full border-4 border-green-200 border-t-green-600 animate-spin"></div>
                            <p className="mt-4 text-gray-400 font-medium">Aggregating reviews...</p>
                        </div>
                    ) : reviews.length === 0 ? (
                        <div className="bg-gray-50 rounded-3xl p-16 text-center border-2 border-dashed border-gray-200">
                            <div className="text-4xl mb-4">✨</div>
                            <h3 className="text-xl font-bold text-gray-700 mb-2">Be the First to Speak</h3>
                            <p className="text-gray-400">Share your journey with us today.</p>
                        </div>
                    ) : (
                        <div className="relative overflow-hidden w-full max-w-4xl mx-auto">
                            <div
                                className="flex transition-transform duration-1000 cubic-bezier(0.4, 0, 0.2, 1)"
                                style={{ transform: `translateX(-${scrollPosition * 100}%)` }}
                            >
                                {reviews.map((review) => (
                                    <div key={review._id} className="min-w-full px-2">
                                        <div className="bg-white rounded-[2.5rem] p-10 md:p-14 border border-gray-100 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden transition-all hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] hover:-translate-y-2">
                                            {/* Decorative Quote */}
                                            <FaQuoteLeft className="absolute top-8 right-10 text-gray-50 text-7xl -z-10" />

                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                                                <div className="space-y-1">
                                                    <h3 className="text-2xl font-black text-gray-900 leading-none">{review.name}</h3>
                                                    <p className="text-sm font-bold text-green-600/60 uppercase tracking-widest px-2 py-1 bg-green-50 inline-block rounded-md">{formatDate(review.createdAt)}</p>
                                                </div>
                                                <StarRating rating={review.rating} size="text-2xl" />
                                            </div>

                                            <div className="relative">
                                                <p className="text-gray-600 text-xl md:text-2xl leading-[1.6] font-medium tracking-tight">
                                                    "{review.comment}"
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Refined Navigation Dots - Sliding Window Logic */}
                            <div className="flex justify-center gap-3 mt-12">
                                {reviews.map((_, idx) => {
                                    // Only show dots within a certain range of the current position
                                    // Show up to 7 dots total for a clean professional look
                                    const maxDots = 7;
                                    const half = Math.floor(maxDots / 2);
                                    let start = Math.max(0, scrollPosition - half);
                                    let end = Math.min(reviews.length, start + maxDots);

                                    if (end - start < maxDots) {
                                        start = Math.max(0, end - maxDots);
                                    }

                                    if (idx < start || idx >= end) return null;

                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => setScrollPosition(idx)}
                                            className={`group relative h-2 transition-all duration-500 ease-out focus:outline-none ${idx === scrollPosition ? 'w-8' : 'w-2'}`}
                                            aria-label={`Go to slide ${idx + 1}`}
                                        >
                                            <span className={`absolute inset-0 rounded-full transition-all duration-500 ${idx === scrollPosition ? 'bg-green-600 shadow-[0_0_15px_rgba(22,163,74,0.4)]' : 'bg-gray-200 group-hover:bg-gray-300'}`}></span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewSection;
